import type { Expense, ExpenseOwner } from './types';

const DB_NAME = 'europa-2026';
const DB_VERSION = 2;
const SETTINGS = 'settings';
const EXPENSES = 'expenses';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SETTINGS)) db.createObjectStore(SETTINGS);
      if (!db.objectStoreNames.contains(EXPENSES)) db.createObjectStore(EXPENSES, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getSetting<T>(key: string): Promise<T | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SETTINGS, 'readonly');
    const request = tx.objectStore(SETTINGS).get(key);
    request.onsuccess = () => resolve((request.result as T | undefined) ?? null);
    request.onerror = () => reject(request.error);
  });
}

async function setSetting<T>(key: string, value: T): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SETTINGS, 'readwrite');
    tx.objectStore(SETTINGS).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function deleteSetting(key: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SETTINGS, 'readwrite');
    tx.objectStore(SETTINGS).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export function getExpenseOwner(): Promise<ExpenseOwner | null> {
  return getSetting<ExpenseOwner>('expenseOwner');
}

export function setExpenseOwner(owner: ExpenseOwner): Promise<void> {
  return setSetting('expenseOwner', owner);
}

export function getWalletKey(): Promise<string | null> {
  return getSetting<string>('walletKeyV02');
}

export function setWalletKey(keyBase64: string): Promise<void> {
  return setSetting('walletKeyV02', keyBase64);
}

export function clearWalletKey(): Promise<void> {
  return deleteSetting('walletKeyV02');
}

export async function listExpenses(): Promise<Expense[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(EXPENSES, 'readonly');
    const request = tx.objectStore(EXPENSES).getAll();
    request.onsuccess = () => resolve((request.result as Expense[]).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    request.onerror = () => reject(request.error);
  });
}

export async function saveExpense(expense: Expense): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(EXPENSES, 'readwrite');
    tx.objectStore(EXPENSES).put(expense);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteExpense(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(EXPENSES, 'readwrite');
    tx.objectStore(EXPENSES).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
